import { storeToRefs } from "pinia";
import { useDialogsStore } from "@/_store/dialogs.module";
import { useCloudAppsStore } from "@/_store/cloud-apps/cloud-apps.module";
import { useGroupsSettingsTabStore } from "@/_store/users-settings/user-groups-tab.module";
import { useUsersSettingsTabStore } from "@/_store/users-settings/users-tab.module";
import { useUsersExceptionsSettingsTabStore } from "@/_store/users-settings/user-exceptions-tab.module";
import { useUserLabelsStore } from "@/_store/users-settings/user-labels.module";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
  isWorkspaceFrozenOrActionRestricted,
} from "@/_helpers/utils";
import { ModalWidth } from "@/constants/modals";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/_store/roles.module";
import { UserAction, UserGroupAction, ExceptionsAction } from "@/constants/users";
import { LabelAction } from "@/constants/devices";
import { i18n } from "@/plugins/i18n";
import AddGroupModal from "@/components/modals/AddGroupModal.vue";
import AddUsersModal from "@/components/modals/AddUsersModal.vue";
import AddAllUsersToProtectionModal from "@/components/modals/AddAllUsersToProtectionModal.vue";
import ImportCsvModal from "@/components/modals/ImportCSVModal.vue";
import AddLabelModal from "@/components/modals/AddLabelModal.vue";
import type { DialogConfig } from "@/_store/dialogs.module";

interface OpenUsersModalpayload {
  action: string;
  disable?: boolean;
  connectedServices?: Array<{ name: string }>;
  exceptionDescription?: string;
}

export function useUserActions() {
  const dialogsStore = useDialogsStore();
  const cloudAppsStore = useCloudAppsStore();
  const userGroupsStore = useGroupsSettingsTabStore();
  const userTabStore = useUsersSettingsTabStore();
  const userExceptionsStore = useUsersExceptionsSettingsTabStore();
  const userLabelsStore = useUserLabelsStore();
  const { connectedServices: storeConnectedServices } = storeToRefs(cloudAppsStore);

  const openAddAllUsersConfirmation = (service: string) => {
    const serviceName = i18n.global.t(`services.${service}`);
    const dialogConfig = confirmationDialogsConfigConstructor({
      item: {
        service,
      },
      action: UserAction.ADD_ALL_USERS_CONFIRMATION,
      disable: isWorkspaceFrozenOrActionRestricted(
        RolePermissionsScope.WORKSPACE_MANAGEMENT,
        WorkspaceManagementScopeSections.USERS
      ),
      text: i18n.global.t(`modals.addAllUsersConfirmation.description`, {
        service: serviceName,
      }),
      callback: userTabStore.addAllUsers,
    });
    dialogConfig.header.title = i18n.global.t(`modals.addAllUsersConfirmation.title`, {
      service: serviceName,
    });
    dialogsStore.openDialog(dialogConfig);
  };

  const openModal = (payload: OpenUsersModalpayload) => {
    const connectedServices = payload.connectedServices || storeConnectedServices.value;

    const actionNotAllowed =
      payload.disable ??
      isWorkspaceFrozenOrActionRestricted(
        RolePermissionsScope.WORKSPACE_MANAGEMENT,
        WorkspaceManagementScopeSections.USERS
      );

    let dialogConfig = {} as DialogConfig;

    switch (payload.action) {
      case UserGroupAction.ADD_NEW_GROUP: {
        dialogConfig = componentDialogsConfigConstructor({
          action: payload.action,
          component: AddGroupModal,
          width: ModalWidth.LARGE,
          callback: userGroupsStore.addNewGroup,
          disable: actionNotAllowed,
        });
        break;
      }
      case ExceptionsAction.ADD_NEW_EXCEPTION: {
        dialogConfig = componentDialogsConfigConstructor({
          item: {
            description:
              payload.exceptionDescription || i18n.global.t("modals.addNewException.description"),
          },
          action: payload.action,
          component: AddUsersModal,
          width: ModalWidth.LARGE,
          callback: userExceptionsStore.addNewException,
          disable: actionNotAllowed,
        });
        break;
      }
      case UserAction.ADD_USERS: {
        dialogConfig = componentDialogsConfigConstructor({
          action: payload.action,
          component: AddUsersModal,
          width: ModalWidth.LARGE,
          callback: userTabStore.addUsers,
          disable: actionNotAllowed,
        });
        break;
      }
      case UserAction.ADD_ALL_USERS: {
        if (connectedServices.length === 1) {
          openAddAllUsersConfirmation(connectedServices[0].name);
          return;
        } else {
          dialogConfig = componentDialogsConfigConstructor({
            item: {
              callback: openAddAllUsersConfirmation,
            },
            action: payload.action,
            component: AddAllUsersToProtectionModal,
            width: ModalWidth.LARGE,
            callback: () => {},
            disable: actionNotAllowed,
            hideFooter: true,
          });
        }
        break;
      }
      case UserAction.IMPORT_USERS_FROM_CSV: {
        dialogConfig = componentDialogsConfigConstructor({
          item: {
            downloadTemplateCallback: userTabStore.downloadUsersExampleCsv,
            uploadCallback: userTabStore.importUsersFromCsv,
          },
          action: payload.action,
          component: ImportCsvModal,
          width: ModalWidth.MEDIUM,
          callback: () => {},
          disable: actionNotAllowed,
          hideFooter: true,
        });
        break;
      }
      case LabelAction.CREATE_LABEL: {
        dialogConfig = componentDialogsConfigConstructor({
          component: AddLabelModal,
          action: payload.action,
          width: ModalWidth.X_SMALL,
          callback: userLabelsStore.createUserLabel,
          disable: actionNotAllowed,
        });
        break;
      }
    }

    dialogsStore.openDialog(dialogConfig);
  };

  return {
    openModal,
  };
}
